import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import SEOHead from '../components/SEOHead';
import { ArrowLeft, Calendar, User, Calculator, TrendingUp, Receipt, Coins, ChevronRight, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { generateBlogSchema } from '../components/StructuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Extract FAQs from content for schema
const extractFAQs = (content) => {
  const faqs = [];
  // Look for FAQ patterns like "### Q:" or "**Q:**" or "### What is"
  const faqRegex = /###\s*(What|How|Is|Can|Do|Should|Are|Why)[^?\n]+\?/gi;
  const matches = content?.match(faqRegex) || [];
  
  matches.slice(0, 5).forEach(question => {
    const cleanQ = question.replace(/^###\s*/, '').trim();
    // Find the answer after this question (next paragraph)
    const qIndex = content.indexOf(question);
    if (qIndex > -1) {
      const afterQ = content.substring(qIndex + question.length);
      const answer = afterQ.split(/\n\n|###/)[0]?.trim().substring(0, 300) || 'See article for details.';
      faqs.push({ q: cleanQ, a: answer.replace(/\n/g, ' ') });
    }
  });
  
  return faqs;
};

// Related calculator links based on blog content
const getRelatedCalculators = (slug, tags) => {
  const cryptoCalcs = [
    { slug: 'crypto-profit-calculator', name: 'Crypto Profit Calculator', icon: Coins },
    { slug: 'bitcoin-profit-calculator', name: 'Bitcoin Profit Calculator', icon: TrendingUp },
    { slug: 'ethereum-profit-calculator', name: 'Ethereum Profit Calculator', icon: TrendingUp },
    { slug: 'crypto-roi-calculator', name: 'Crypto ROI Calculator', icon: TrendingUp },
    { slug: 'crypto-tax-calculator', name: 'Crypto Tax Calculator', icon: Receipt },
  ];
  
  const isCryptoPost = tags?.some(t => 
    ['Crypto', 'Bitcoin', 'Ethereum', 'Calculator', 'ROI', 'Tax', 'Gains', 'Profit'].includes(t)
  );
  
  return isCryptoPost ? cryptoCalcs : [];
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch (error) {
        console.error('Error fetching blog post:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <Skeleton className="h-8 w-32 mb-8" />
          <Skeleton className="h-12 w-full mb-4" />
          <Skeleton className="h-4 w-64 mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-2xl font-bold mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-primary hover:underline">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`${post.title} | ToolsForge Blog`}
        description={post.excerpt}
        keywords={post.tags?.join(', ')}
        canonical={`${window.location.origin}/blog/${slug}`}
      />
      <div className="min-h-screen pt-24 pb-16 px-6" data-testid="blog-post-page">
      <article className="max-w-3xl mx-auto">
        {/* Breadcrumbs for SEO */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-primary transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/blog" className="hover:text-primary transition-colors">Blog</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-foreground truncate max-w-[200px]">{post.title?.split(' ').slice(0, 4).join(' ')}...</span>
        </nav>

        {/* Back Button */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
          data-testid="back-to-blog"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Updated for 2026 Badge */}
          <div className="mb-4">
            <Badge variant="outline" className="px-3 py-1 border-green-500/30 text-green-400">
              <RefreshCw className="w-3 h-3 mr-2" />
              Updated for 2026
            </Badge>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold mb-4 font-['Plus_Jakarta_Sans']" data-testid="post-title">
            {post.title}
          </h1>

          {/* Meta */}
          <div className="flex items-center gap-4 mb-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {formatDate(post.created_at)}
            </div>
            <span>•</span>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author}
            </div>
          </div>

          {/* Tags */}
          <div className="flex gap-2 mb-8">
            {post.tags?.map((tag) => (
              <Badge key={tag} variant="outline">
                {tag}
              </Badge>
            ))}
          </div>

          {/* Content */}
          <div 
            className="prose prose-invert prose-lg max-w-none"
            data-testid="post-content"
          >
            <ReactMarkdown
              components={{
                h1: ({node, ...props}) => <h1 className="text-3xl font-bold mt-8 mb-4 text-foreground" {...props} />,
                h2: ({node, ...props}) => <h2 className="text-2xl font-semibold mt-6 mb-3 text-foreground" {...props} />,
                h3: ({node, ...props}) => <h3 className="text-xl font-semibold mt-4 mb-2 text-foreground" {...props} />,
                p: ({node, ...props}) => <p className="text-muted-foreground mb-4 leading-relaxed" {...props} />,
                li: ({node, ...props}) => <li className="text-muted-foreground ml-4" {...props} />,
                ul: ({node, ...props}) => <ul className="list-disc list-inside mb-4 space-y-1" {...props} />,
                ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-4 space-y-1" {...props} />,
                strong: ({node, ...props}) => <strong className="font-bold text-foreground" {...props} />,
                a: ({node, href, ...props}) => (
                  <Link to={href} className="text-primary hover:underline" {...props} />
                ),
                code: ({node, ...props}) => <code className="bg-muted px-1 py-0.5 rounded text-sm" {...props} />,
                pre: ({node, ...props}) => <pre className="bg-muted p-4 rounded-lg overflow-x-auto mb-4" {...props} />,
                blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground mb-4" {...props} />,
                table: ({node, ...props}) => <table className="w-full border-collapse mb-4" {...props} />,
                th: ({node, ...props}) => <th className="border border-border px-4 py-2 bg-muted text-left" {...props} />,
                td: ({node, ...props}) => <td className="border border-border px-4 py-2" {...props} />,
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {/* Related Calculators Section - Internal Linking */}
          {getRelatedCalculators(slug, post.tags).length > 0 && (
            <div className="mt-12 p-6 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl border border-primary/20">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-primary" />
                Try Our Free Crypto Calculators
              </h2>
              <p className="text-muted-foreground mb-4 text-sm">
                Put what you learned into practice with our instant, free calculators:
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {getRelatedCalculators(slug, post.tags).map(calc => (
                  <Link
                    key={calc.slug}
                    to={`/tools/${calc.slug}`}
                    className="flex items-center gap-3 p-3 bg-background/50 rounded-lg border border-border hover:border-primary/50 hover:bg-background transition-all group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <calc.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <span className="font-medium group-hover:text-primary transition-colors">{calc.name}</span>
                      <span className="block text-xs text-muted-foreground">Free • Instant • No signup</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Hub Page CTA */}
          <div className="mt-8 p-4 bg-muted/30 rounded-lg border border-border text-center">
            <p className="text-muted-foreground mb-2">
              Explore all our cryptocurrency tools in one place
            </p>
            <Link 
              to="/crypto-calculators" 
              className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
            >
              Visit Crypto Calculators Hub →
            </Link>
          </div>
        </motion.div>
      </article>

      {/* FAQ Schema for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.excerpt,
            "author": { "@type": "Person", "name": post.author },
            "datePublished": post.created_at,
            "dateModified": new Date().toISOString(),
            "publisher": {
              "@type": "Organization",
              "name": "ToolsForge",
              "url": "https://toolsforgeapp.com"
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://toolsforgeapp.com/blog/${slug}`
            }
          })
        }}
      />
      
      {/* Breadcrumb Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://toolsforgeapp.com" },
              { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://toolsforgeapp.com/blog" },
              { "@type": "ListItem", "position": 3, "name": post.title, "item": `https://toolsforgeapp.com/blog/${slug}` }
            ]
          })
        }}
      />
      </div>
    </>
  );
};

export default BlogPostPage;
